import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Form } from "semantic-ui-react";
import { Helmet } from "react-helmet-async";

import { getToken, isBrowser } from "../../../services/auth";
import { config } from '../../../config/env';

import InputText from "../../../components/inputText/InputText";
import TextArea from "../../../components/textArea/TextArea";
import Button from "../../../components/button/Button";
import Modal from "../../../components/modal/Modal";
import Loader from "../../../components/modal/Loader";
import MarkdownEditor from "../../../components/markdownEditor/MarkdownEditor";

const AddTopic = () => {
  // DEFAULT STATE
  const defaultState = {
    isLoading: false,
    showModal: false,
    modalTitle: "",
    modalSubText: "",
    modalIcon: "",
    confirmButtonText: "",
    confirmButtonAction: null,
    previewMarkdown: false
  };

  // HOOKS
  const [state, setState] = useState(defaultState);
  const [images, setImages] = useState([])
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  // EVENT HANDLERS
  const onSubmit = async (e) => {
    const newTopic = {
      name: e.name,
      description: e.description,
      image_filenames: []
    };
    if(e.content){
      newTopic.content = e.content
    }
    if(e.image_filenames && e.image_filenames.length > 0) {
      newTopic.image_filenames = e.image_filenames
    }

    if (isBrowser()) {
      setState(prev => ({...prev, isLoading: true}))
      fetch(`${config.apiBaseUrl}/api/v1/admin/topics/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify(newTopic),
      }).then(async(res) => {
        if (res.status === 200) {
          setState(prev => ({...prev, 
            modalIcon: "success",
            modalTitle: "Successful",
            modalSubText: "Topic was added successfully",
            confirmButtonText: "Go to topics",
            confirmButtonAction: handleConfirmButtonAction
          }));
        } else {
          let errorMessage = "Something went wrong.";
          try {
            const errorData = await res.json();
            errorMessage = errorData?.message || JSON.stringify(errorData);
          } catch (jsonErr) {
            try {
              errorMessage = await res.text();
            } catch (textErr) {
              errorMessage = "Unable to read error message.";
            }
          }
          setState(prev => ({...prev, 
            modalIcon: "error",
            modalTitle: "Error",
            modalSubText: errorMessage,
            confirmButtonText: "Close",
            confirmButtonAction: handleResetModal
          }));
        }
      }).catch((error) => {
        setState(prev => ({...prev, 
          modalIcon: "error",
          modalTitle: "Error",
          modalSubText: error.toString(),
          confirmButtonText: "Close",
          confirmButtonAction: handleResetModal
        }));
      }).finally(() => {
        setState(prev => ({...prev, 
          isLoading: false,
          showModal: true
        }));
      });
    }
  };

  const onCancel = () => {
    navigate("/ava-admin/topics");
  };

  const handleConfirmButtonAction = () => {
    navigate("/ava-admin/topics");
  };

  const handleResetModal = () => {
    setState(prev => ({...prev, 
      showModal: false,
      confirmButtonAction: null
    }));
  };

  // JSX
  return (
    <>
      <Helmet>
        <title>Avanade Research - Admin</title>
      </Helmet>
      <div page="topics">
        <div className="w-[calc(100%-40px)] min-h-[calc(100%-40px)] m-[20px] bg-white rounded-2xl p-10 shadow-lg text-sm">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex w-full">
              <div className="block w-full">
                <div className="text-lg font-semibold">Add Topic</div>
                <div className="text-gray-600">
                  Add a defined subject or theme to guide the direction of certain research and development effort.
                </div>
              </div>
            </div>

            <div className="mt-10">
              <Form>
                <Form.Field>
                  <div className="mt-3">
                    <InputText
                      type="text"
                      label="Name"
                      id="name"
                      script={register("name", { required: true })}
                      error={errors.name ? "Topic name is required" : ""}
                    />
                  </div>
                  <div className="mt-5">
                    <TextArea
                      label="Description"
                      id="description"
                      script={register("description", { required: false })}
                      rows="4"
                    />
                  </div>
                  <MarkdownEditor 
                    images={images} setImages={setImages}
                    isLoading={state.isLoading} setIsLoading={(loading) => setState(prev => ({...prev, isLoading: loading}))}
                    getValues={getValues} setValue={setValue}
                    previewMarkdown={state.previewMarkdown} setPreviewMarkdown={(previewMarkdown) => setState(prev => ({...prev, previewMarkdown: previewMarkdown}))}
                    register={register} errors={errors}
                  />
                </Form.Field>
                <div className="flex justify-end mt-7">
                  <Button type="button" label="Cancel" color="gray-200" text="gray-800" onClick={onCancel} />
                  <Button type="submit" label="Submit" className="ml-4" onClick={handleSubmit(onSubmit)} />
                </div>
              </Form>

              {state.isLoading && <Loader />}

              {state.showModal && (
                <Modal
                  title={state.modalTitle}
                  subtext={state.modalSubText}
                  confirmButtonText={state.confirmButtonText}
                  confirmButtonAction={state.confirmButtonAction}
                  icon={state.modalIcon}
                  onCancel={handleResetModal}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTopic;
